import QRCode from 'qrcodejs2'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { GET } from './Request'
import { GET_ADMIT_CARD_DATA } from '../const/api'

/**
 * AdmitCardGenerator - Generates and downloads admit cards in the background
 * Designed for high scalability (1M+ concurrent users)
 */
class AdmitCardGenerator {
  constructor() {
    this.isGenerating = false
    this.container = null
  }

  /**
   * Main method to generate and download admit card
   * @param {string} unit - The unit code (e.g., 'A', 'B', etc.)
   * @param {Function} onProgress - Callback for progress updates
   * @param {Function} onError - Callback for error handling
   * @returns {Promise<void>}
   */
  async generateAdmitCard(unit, onProgress = null, onError = null) {
    if (this.isGenerating) {
      throw new Error('Admit card generation already in progress')
    }

    this.isGenerating = true

    try {
      // Step 1: Fetch admit card data
      if (onProgress) onProgress('Fetching admit card data...', 10)
      const studentInfo = await this.fetchAdmitCardData(unit)

      // Step 2: Pre-load static images
      // if (onProgress) onProgress('Loading resources...', 25)
      // const staticImages = await this.preloadStaticImages()

      // Step 3: Create temporary container
      if (onProgress) onProgress('Preparing admit card...', 35)
      this.createContainer()

      // Step 4: Render admit card HTML
      if (onProgress) onProgress('Rendering admit card...', 50)
      const qrText = `${process.env.VUE_APP_FRONTEND_URL}check-admit-card/${studentInfo.hash_key}`
      this.renderAdmitCard(studentInfo, unit, qrText)

      // Step 5: Generate QR code
      if (onProgress) onProgress('Generating QR code...', 60)
      await this.generateQRCodeInContainer(qrText)

      // Step 6: Wait for fonts and images to load
      if (onProgress) onProgress('Loading fonts and images...', 70)
      await this.waitForResourcesLoaded()

      // Step 7: Generate PDF
      if (onProgress) onProgress('Generating PDF...', 85)
      await this.generatePDF(studentInfo.exam_roll,unit)

      // Step 8: Cleanup
      if (onProgress) onProgress('Download complete!', 100)
      this.cleanup()

      this.isGenerating = false
    } catch (error) {
      this.cleanup()
      this.isGenerating = false
      if (onError) {
        onError(error)
      }
      throw error
    }
  }

  /**
   * Pre-load static images as base64 to avoid CORS issues
   */
  // async preloadStaticImages() {
  //   const baseUrl = 'https://cu-admission.sgp1.digitaloceanspaces.com/logo'

  //   // Load images and convert to base64
  //   const loadImageAsBase64 = async (url) => {
  //     return new Promise((resolve, reject) => {
  //       const img = new Image()
  //       img.crossOrigin = 'anonymous'
  //       img.onload = () => {
  //         const canvas = document.createElement('canvas')
  //         canvas.width = img.width
  //         canvas.height = img.height
  //         const ctx = canvas.getContext('2d')
  //         ctx.drawImage(img, 0, 0)
  //         try {
  //           const base64 = canvas.toDataURL('image/png')
  //           resolve(base64)
  //         } catch (e) {
  //           console.error('Failed to convert image to base64:', e)
  //           resolve(url) // Fallback to original URL
  //         }
  //       }
  //       img.onerror = () => {
  //         console.error('Failed to load image:', url)
  //         resolve(url) // Fallback to original URL
  //       }
  //       img.src = url
  //     })
  //   }

  //   const [logo, background] = await Promise.all([
  //     loadImageAsBase64(`${baseUrl}/admit_logo.png`),
  //     loadImageAsBase64(`${baseUrl}/cu_bg_logo.png`)
  //   ])

  //   return { logo, background }
  // }

  /**
   * Fetch admit card data from API
   */
  async fetchAdmitCardData(unit) {
    const unitUpper = unit.toUpperCase()
    const response = await GET(GET_ADMIT_CARD_DATA + unitUpper)

    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch admit card data')
    }

    const data = response.data

    // Format download date
    const downloadDate = data.download_date || this.formatDate(new Date())
    const downloadTime = data.download_time || this.formatTime(new Date())

    return {
      hash_key: data.hash_key,
      applicant_no: String(data.application_id),
      exam_roll: String(data.exam_roll),
      name: data.name,
      father_name: data.father_name,
      mother_name: data.mother_name,
      exam_center: data.exam_center,
      exam_date: data.exam_date,
      exam_time: data.exam_time,
      photo: data.photo,
      signature: data.signature,
      co_ordinator_signature: data.co_ordinator_signature,
      unit_co_ordinator: data.unit_co_ordinator,
      medium: data.medium,
      admitcard_download_date: downloadDate,
      admitcard_download_time: downloadTime
    }
  }

  /**
   * Format date for admit card
   */
  formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  /**
   * Format time for admit card
   */
  formatTime(date) {
    let hour = date.getHours()
    const mins = String(date.getMinutes()).padStart(2, '0')
    let amPm = 'AM'

    if (hour > 11) {
      amPm = 'PM'
      if (hour > 12) {
        hour -= 12
      }
    }

    return `${hour}:${mins} ${amPm}`
  }

  /**
   * Create temporary container for rendering
   * A4 size: 210mm x 297mm = 793.7px x 1122.5px at 96 DPI
   */
  createContainer() {
    // Remove existing container if any
    this.cleanup()

    // Create new container
    // A4 dimensions at 96 DPI (standard web DPI)
    const a4Width = 794 // 210mm in pixels
    const a4Height = 1123 // 297mm in pixels

    this.container = document.createElement('div')
    this.container.id = 'admit-card-generator'
    this.container.style.position = 'absolute'
    this.container.style.left = '-9999px'
    this.container.style.top = '-9999px'
    this.container.style.width = a4Width + 'px'
    this.container.style.height = a4Height + 'px'
    this.container.style.backgroundColor = 'white'
    this.container.style.boxSizing = 'border-box'

    document.body.appendChild(this.container)
  }

  /**
   * Generate QR code in the container
   */
  async generateQRCodeInContainer(qrText) {
    return new Promise((resolve) => {
      const qrCodeContainer = this.container.querySelector('#qrCodeUrl')

      if (qrCodeContainer) {
        // Clear any existing content
        qrCodeContainer.innerHTML = ''

        // Create canvas div for QR code
        const canvasDiv = document.createElement('div')
        canvasDiv.id = 'canvas'
        qrCodeContainer.appendChild(canvasDiv)

        // Generate QR code
        new QRCode('canvas', {
          text: qrText,
          width: 100,
          height: 100,
          colorDark: '#000000',
          colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.H
        })

        // Wait for QR code to render
        setTimeout(() => resolve(), 200)
      } else {
        resolve()
      }
    })
  }

  /**
   * Render admit card HTML
   */
  renderAdmitCard(studentInfo, unit, qrText) {
    const unitUpper = unit.toUpperCase()
   /*
   const unit_map = {
    "A": "এ ইউনিট",
    "B": "বি ইউনিট",
    "B1": "বি-১ ইউনিট",
    "B2": "বি-২ ইউনিট",
    "C": "সি ইউনিট",
    "D": "ডি ইউনিট",
    "D1": "ডি-১ ইউনিট"
  };
  */

  const unit_map = {
    "A": "<span class='english-text'>A</span> ইউনিট",
    "B": "<span class='english-text'>B</span> ইউনিট",
    "B1": "<span class='english-text'>B1</span> উপ-ইউনিট",
    "B2": "<span class='english-text'>B2</span> উপ-ইউনিট",
    "C": "<span class='english-text'>C</span> ইউনিট",
    "D": "<span class='english-text'>D</span> ইউনিট",
    "D1": "<span class='english-text'>D1</span> উপ-ইউনিট"
  };


  let selected_unit;

  if (unitUpper in unit_map) {
    // Unit Found: Assign the direct value lookup result
    selected_unit = unit_map[unitUpper];
  } else {
    // Unit Not Found: Throw the specified error
    throw new Error(`Unit ${unitUpper} not mapped like this or not found.`);
  }

        const html = `
<style>
@import url('https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&family=Tiro+Bangla:ital@0;1&family=Victor+Mono:ital,wght@0,500;1,500&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-size: 12px;
  line-height: 1.3;
  color: black;
}

.bangla-text { font-family: "Tiro Bangla", serif; font-weight: 400; }
.bangla-text-bold { font-family: "Tiro Bangla", serif; font-weight: 600; }
.english-text { /*font-family: "Courier Prime", monospace;*/ font-family: "Roboto Condensed", sans-serif; font-weight: 400; }
.english-text-bold { /*font-family: "Courier Prime", monospace;*/ font-family: "Roboto Condensed", sans-serif; font-weight: 700; }

.victor-mono-digit {
  font-family: "Victor Mono", monospace;
  font-optical-sizing: auto;
  font-weight: 400;
  font-style: normal;
}

.container { width: 100%; padding: 70px 50px 10px 50px; margin: auto; }
.row { display: flex; flex-wrap: wrap; margin: 0; }
.col-md-2, .col-md-6, .col-md-8, .col-md-12 { padding: 0 5px; margin-bottom: 5px; }
.col-md-2 { flex: 0 0 16.6667%; max-width: 16.6667%; }
.col-md-6 { flex: 0 0 50%; max-width: 50%; }
.col-md-8 { flex: 0 0 66.6667%; max-width: 66.6667%; }
.col-md-12 { flex: 0 0 100%; max-width: 100%; }

.text-center { text-align: center; }
.text-left { text-align: left; }
.text-right { text-align: right; }
.font-weight-bold { font-weight: bold; }

.text-center { text-align: center; }
.text-left { text-align: left; }
.text-right { text-align: right; }
.font-weight-bold { font-weight: bold; }

.font-12{
  font-size:12px;
}
.font-14{
  font-size:11px;
}
.university-name {
  font-size: 23px;
  line-height: 1.4;
  margin-bottom: 2px;
}
.admission-title { font-size: 12px; margin-bottom: 5px; }
.university-logo { max-width: 100%;height: auto;max-height: 105px;float: left; }


.roll-application-row {
  display: flex;
  justify-content: space-between; /* left & right most */
  align-items: center;
  width: 100%;          /* full width */
  margin: 10px 0 5px 0;            /* remove Bootstrap row margin */
  padding: 0;           /* remove Bootstrap row padding */
}

.roll-number-section,
.application-id-section {
  font-size: 14px;
  color: black;
  margin: 0;
  padding: 0;
}



.qrcode { width: 80px; height: 80px; margin: auto; }

.unit-text {
  color: black;
  font-size: 14px;
  margin-top: 20px;
}

.student-details-row {
  display: flex;
  width: 100%;
  margin: 10px 0;
  align-items: flex-start; /* aligns top edges */
}

.student-info-column {
  flex: 1 1 auto; /* take remaining space */
  padding-right: 15px;
  min-width: 0;
}

.photo-signature-column {
  flex: 0 0 auto; /* fixed width, don't shrink or grow */
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}



        .student-info-row {
          color: black;
          padding: 8px 0;
          font-size: 13px;
          line-height: 1.3;
        }

        .exam-info-box {
          color: black;
          padding: 8px;
          text-align: center;
          border: 1.5px solid black;
          max-width: 250px;
          margin-top: 10px;
          font-size: 13px;
          line-height: 1.6;
        }

        .student-photo {
          width: 130px;
          height: auto;
          border: 1px solid gray;
          padding: 1px;
          margin-bottom: 10px;
        }

        .student-signature {
          width: 110px;
          height: auto;
          display: block;
        }

        .signature-label {
          border-top: 1px dashed black;
          width: 110px;
          margin: 1px auto 0;
          font-size: 11px;
          padding-top: 1px;
        }


.instructions-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          width: 95%;
          margin: 10px 0 0 10px;
        }


        .instructions-list {
          width: 100%;
          text-align: justify;
          padding-left: 18px; 
          list-style-position: outside;
        }

        .instructions-list li {
          margin-bottom: 5px;
          line-height: 1.4;
          font-size: 11px;
        }

.count-desc { font-family: 'Times New Roman', Times, serif; font-weight: bold; }
.roll-digit { border: 1px solid black; padding: 1px 4px; font-size: 14px; display: inline-block; margin-right: 2px; }
.roll-digit-last { border-right: 1px solid black;font-size: 14px; }
span[style*="border: 2px solid black"] { display: inline-block; }

p { margin: 2px 0; }

.subject-choice-header {
  display: flex;
  flex-direction: column; /* stack items vertically */
  justify-content: center; /* center vertically (optional) */
  align-items: center;     /* center horizontally */
  gap: 5px; /* space between title and badge */
  margin-top: 10px;
  margin-bottom: 8px;
}

.unit-quota-badge {
  border: 1px solid black;
  border-radius: 5px;
  padding: 2px 6px;
  font-size: 12px;
  display: inline-block;
}

/* Container rows */
.subject-choice-row {
  display: flex;
  flex-wrap: nowrap;
  border-bottom: 1px solid #ddd;
  padding: 4px 0;
}

/* Header row */
.subject-choice-header-row {
  background-color: #eef7fb;
  font-weight: bold;
}

/* Cells */
.subject-choice-cell {
  padding: 2px 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
}

/* Specific column widths */
.code-cell { flex: 0 0 25%; text-align: center; }
.title-cell { flex: 0 0 50%; text-align: left; }
.serial-cell { flex: 0 0 25%; text-align: center; }

/* Optional styling for card body */
.card-body {
  padding: 5px 10px;
}

.applicant-info-row {
  display: flex;
  flex-wrap: nowrap;
  margin-bottom: 4px;
  font-size: 12px;
}

.applicant-info-label {
  flex: 0 0 30%; /* adjust width of label column */
  font-weight: bold;
}

.applicant-info-separator {
  flex: 0 0 5%;  /* small space for ':' */
  text-align: center;
}

.applicant-info-value {
  flex: 1 0 65%; /* remaining width */
}


.admit-card-title {
  width: 100%;
  text-align: center;
  font-size: 29px;
  color: black;
  margin: 10px 0 5px 0;
  line-height: 1;
}

.unit-title{
  width: 100%;
  text-align: center;
  font-size: 21px;
  color: black;
  margin: 10px 0 5px 0;
  line-height: 1;
}

.instructions-wrapper {
  font-size: 12px; /* override works fine now */
  margin: 0;
  padding: 0;
}

.instruction-item {
  margin-bottom: 6px;
  line-height: 1.5;
  display: block;
}


.instructions-wrapper a {
  text-decoration: none;
  color: #007bff;
}


.footer-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          width: 100%;
          position:relative;
          bottom:-80px;

        }

        .download-info {
          color: black;
          font-size: 12px;
          line-height: 1.4;
        }

        .download-date {
          font-weight: bold;
        }

        .powered-by-text {
          font-style: italic;
          font-size: 10px;
        }

        .coordinator-signature {
          width: 120px;
          height: auto;
          max-height:80px;
          margin-bottom: 3px;
        }

        .coordinator-info {
          text-align: center;
          line-height: 1.3;
          color: black;
          font-size: 12px;
        }


/* Watermark styles */
.admit-card-generator-wrapper {
  position: relative;
  overflow: hidden; /* Ensure watermark doesn't spill out */
}

.admit-card-generator-wrapper::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%; /* Adjust size as needed */
  height: 100%; /* Adjust size as needed */
  background-image: url('https://cu-admission.sgp1.cdn.digitaloceanspaces.com/logo/cu_watermark.png');
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 50%; /* Adjust size of the watermark */
  opacity: 0.7; /* Adjust opacity for watermark effect */
  transform: translate(-50%, -50%); /* Center the watermark */
  pointer-events: none; /* Allow interaction with content above */
  z-index: -1; /* Place it behind the content */
}

/* Force A4 page size for print */
@page { size: A4; margin: 15mm 10mm; }
</style>

<div class="admit-card-generator-wrapper">

<div class="container">

  <!-- Header -->
    <div class="row">
      <div class="col-md-2 text-center">
        <img alt="Logo" class="university-logo" src="https://cu-admission.sgp1.digitaloceanspaces.com/logo/admit_logo.png">
      </div>
      <div class="col-md-8 text-center">
        <div class="bangla-text university-name">চট্টগ্রাম বিশ্ববিদ্যালয়</div>
        <div class="bangla-text admission-title font-12">১ম বর্ষ স্নাতক (সম্মান) ভর্তি পরীক্ষা, শিক্ষাবর্ষ: ২০২৬-২০২৭</div>
        <div class="bangla-text admit-card-title">প্রবেশ পত্র</div>
        <div class="bangla-text unit-title">${selected_unit}</div>
      </div>
      <div class="col-md-2 text-center">
        <div class="qrcode" id="qrCodeUrl"></div>
      </div>
    </div>


    <div class="row">
      <div class="col-md-12 text-center subject-choice-header">
        <div class="bangla-text font-12">
            পরীক্ষা কেন্দ্রে প্রবেশের জন্য এই প্রবেশপত্রের এক কপি প্রিন্ট অবশ্যই সাথে আনতে হবে। উত্তীর্ণ পরীক্ষার্থীদের সাক্ষাৎকার ও অন্যান্য ভর্তি </br> কার্যক্রমেও এই প্রবেশপত্রের প্রয়োজন হবে বিধায়  ডাউনলোডকৃত প্রবেশপত্রটির ডিজিটাল ও প্রিন্ট কপি সংরক্ষণ করতে হবে। 
        </div>
        
      </div>
    </div>


    <!-- Roll & Application -->
    <div class="roll-application-row">
      <div class="roll-number-section">
        Admission Roll: <b>${this.renderDigits(studentInfo.exam_roll)}</b>
      </div>
      <div class="application-id-section">
        Application ID: <b>${this.renderDigits(studentInfo.applicant_no)}</b>
      </div>
    </div>



    <!-- Row 4: Student Info & Photo -->
      <div class="student-details-row">
        <div class="student-info-column">
          <div class="bangla-text student-info-row">
            পরীক্ষার্থীর নাম: <span class="english-text">${studentInfo.name}</span>
          </div>
          <div class="bangla-text student-info-row">
            পিতার নাম: <span class="english-text">${studentInfo.father_name}</span>
          </div>
          <div class="bangla-text student-info-row">
            মাতার নাম: <span class="english-text">${studentInfo.mother_name}</span>
          </div>
          <div class="bangla-text exam-info-box">
            পরীক্ষার অঞ্চল: <span class="english-text">${studentInfo.exam_center}</span><br>
            পরীক্ষার তারিখ: <span class="english-text">${studentInfo.exam_date}</span><br>
            সময়: <span class="english-text">${studentInfo.exam_time}
          </div>
        </div>
        <div class="photo-signature-column">
          <img src="${studentInfo.photo}" alt="Student Photo" class="student-photo">
          <img src="${studentInfo.signature}" alt="Signature" class="student-signature">
          <div class="bangla-text signature-label">পরীক্ষার্থীর স্বাক্ষর</div>
        </div>
      </div>

      <!-- Row 5: Instructions --> 
      <div class="row">
        <div class="col-md-12">
          <div class="bangla-text font-12">নির্দেশাবলী: </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="bangla-text instructions-list font-12 instructions-wrapper">
            
            <div class="instruction-item">১। উত্তরপত্রে লেখার কাজে কালো কালির বলপয়েন্ট কলম ব্যবহার করতে হবে। পেন্সিল অথবা তরল কালির কলম ব্যবহার সম্পূর্ণভাবে নিষিদ্ধ।</div>

            <div class="instruction-item">২। ⁠পরীক্ষা কেন্দ্রে প্রবেশের জন্য ও পরীক্ষায় অংশগ্রহনের জন্য এই প্রবেশপত্রের এক কপি রঙিন অথবা সাদাকালো প্রিন্ট এবং উচ্চ মাধ্যমিক শিক্ষা বোর্ডের মূল রেজিস্ট্রেশন কার্ড অথবা <span class="english-text">A</span>-লেভেলের <span class="english-text">Statement of Entry</span>-র মূল কপি সাথে আনতে হবে। পরীক্ষায় অংশগ্রহনের সময় পরীক্ষা কেন্দ্রের পরিদর্শককে চাহিদা অনুযায়ী এ সকল কাগজপত্র প্রদর্শন করতে হবে।</div>

            <div class="instruction-item">৩। পরীক্ষায় সহায়ক হিসাবে ব্যবহারের জন্য <span class="english-text">FX-100</span> বা এর নিচের মানের ক্যালকুলেটর ব্যবহার করা যাবে, তবে সিম বা অন্য কোন যোগাযোগ প্রযুক্তি সমৃদ্ধ এবং প্রোগ্রামেবল ক্যালকুলেটর ব্যবহার করা যাবে না। পরীক্ষার সময় মোবাইল ফোন, স্মার্ট ওয়াচ, যে কোন ধরনের যোগাযোগ যন্ত্র বা অন্য ইলেকট্রনিক ডিভাইস/গ্যাজেট সাথে রাখা সম্পূর্ণ নিষিদ্ধ।</div>

            <div class="instruction-item">৪। ⁠সনাক্তকরণের স্বার্থে পরীক্ষার্থীর মুখমন্ডল ও উভয় কান দৃশ্যমান থাকতে হবে।</div>

            <div class="instruction-item">৫। ⁠আবেদনের সময় (অথবা পরবর্তীতে সংশোধনের সময়) ভর্তির ওয়েবসাইটে আপলোডকৃত ছবি ও স্বাক্ষরই চুড়ান্ত বলে গণ্য হবে। সংশোধনের নির্ধারিত সময় শেষ হবার পর কোন অবস্থাতেই ছবি বা স্বাক্ষর পরিবর্তন করা যাবে না।</div>

            <div class="instruction-item">৬। পরীক্ষার হলে পরিদর্শক বা প্রধান পরিদর্শকের সিদ্ধান্তই চুড়ান্ত বলে গণ্য হবে।</div>

            <div class="instruction-item">৭। পরীক্ষার কেন্দ্র, ভবন, কক্ষ নম্বর; পরীক্ষার ফলাফল; সাক্ষাৎকারের তারিখ ও স্থান; সাময়িক ও চুড়ান্ত ভর্তির যাবতীয় তথ্য ইত্যাদি উপযুক্ত সময়ে অ্যাডমিশান ওয়েবসাইটে ( <span class="english-text">admission.cu.ac.bd</span> ) প্রদর্শিত হবে। পরীক্ষার্থীদের নিজ নিজ দায়িত্বে যথাসময়ে ওয়েবসাইট হতে তথ্য সংগ্রহ করতে হবে।</div>

            <div class="instruction-item">৮। ক্ষেত্রবিশেষে পরীক্ষার্থীদের প্রদত্ত মোবাইল ফোনে ক্ষুদ্র বার্তার মাধ্যমে জরূরী সংবাদ প্রদান করা হবে, আবেদনের সময় থেকে ভর্তি প্রক্রিয়া সম্পূর্ণ শেষ না হওয়া পর্যন্ত মোবাইল ফোনটি সচল রাখার দায়িত্ব সংশ্লিষ্ট পরীক্ষার্থীর।</div>

        </div>
      </div>

      


      



           <div class="footer-row">
            <div>
              <div class="bangla-text download-info">
                ডাউনলোডের তারিখ: <span class="download-date english-text">${studentInfo.admitcard_download_date}</span><br><br>
                <span class="english-text powered-by-text">Powered by <b>ICT Cell, University of Chittagong</b></span>
              </div>
            </div>
            <div style="text-align: center;">
              <img src="${studentInfo.co_ordinator_signature}" alt="Coordinator Signature" class="coordinator-signature">
              <div class="bangla-text coordinator-info">
                ${studentInfo.unit_co_ordinator}<br>
                কো-অর্ডিনেটর<br>
                <span class="english-text font-14">${unitUpper}</span> ${unitUpper.length > 1 ? 'উপ-' : ''}ইউনিট ভর্তি কমিটি
              </div>
            </div>
          </div>







</div>


</div>

</div>

`

    this.container.innerHTML = html
  }

  /**
   * Render digit boxes for roll number and application ID
   */
  renderDigits(number) {
    const digits = String(number).split('')
    return digits.map((digit, index) => {
      const isLast = index === digits.length - 1
      return `<span class="roll-digit ${isLast ? 'roll-digit-last' : ''}">${digit}</span>`
    }).join('')
  }

  /**
   * Wait for all resources (fonts, images) to load
   */
  async waitForResourcesLoaded() {
    // Load font
    await document.fonts.load('16px "Tiro Bangla"')

    // Wait for all images to load
    const images = this.container.querySelectorAll('img')
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) {
          return Promise.resolve()
        }
        return new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = () => {
            console.warn('Image failed to load:', img.src)
            resolve() // Resolve anyway to not block
          }
          // Timeout after 5 seconds
          setTimeout(resolve, 5000)
        })
      })
    )

    // Optimize images by reducing their display size
    await this.optimizeImages()

    // Small delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 300))
  }

  /**
   * Optimize images for smaller PDF size
   */
  async optimizeImages() {
    const images = this.container.querySelectorAll('img')

    for (const img of images) {
      // Skip if image failed to load
      if (!img.complete || !img.naturalWidth) continue

      try {
        // Create a canvas to resize and compress the image
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        // Get current display dimensions
        const rect = img.getBoundingClientRect()
        const maxWidth = rect.width * 1.5 // 1.5x for retina displays
        const maxHeight = rect.height * 1.5

        // Calculate new dimensions maintaining aspect ratio
        let width = img.naturalWidth
        let height = img.naturalHeight

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = width * ratio
          height = height * ratio
        }

        canvas.width = width
        canvas.height = height

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height)

        // Convert to compressed JPEG (except for signatures which need transparency)
        const isSignature = img.alt && img.alt.toLowerCase().includes('signature')
        const quality = isSignature ? 0.9 : 0.85
        const format = isSignature ? 'image/png' : 'image/jpeg'

        const compressedDataUrl = canvas.toDataURL(format, quality)

        // Replace original image with compressed version
        img.src = compressedDataUrl
      } catch (error) {
        console.warn('Failed to optimize image:', error)
        // Continue with original image if optimization fails
      }
    }
  }

  /**
   * Generate PDF from the rendered HTML
   * Optimized for smaller file size and faster downloads
   */
  async generatePDF(examRoll, unit) {
    const element = this.container.querySelector('.admit-card-generator-wrapper')

    element.style.paddingBottom = "80px";

    // Render to canvas with optimized settings
    const canvas = await html2canvas(element, {
      scale: 1.5, // Balanced quality vs size (was 2)
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      //removeContainer: true,
      //imageTimeout: 0
    })

    // Convert to JPEG with compression for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.85) // 85% quality JPEG

    // Create PDF with compression
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true // Enable PDF compression
    })

    // Calculate dimensions to fit A4
    const pdfWidth = pdf.internal.pageSize.getWidth()
    //const pdfHeight = (canvas.height * pdfWidth) / canvas.width
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Add image with JPEG format (smaller than PNG)
    //pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST')

    const imgProps = pdf.getImageProperties(imgData);
    const pdfImgHeight = (imgProps.height * pdfWidth) / imgProps.width;

    let position = 0;
    let remainingHeight = pdfImgHeight;

    // Add pages
    while (remainingHeight > 0) {
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfImgHeight);
      remainingHeight -= pdfHeight;
      position -= pdfHeight;

      if (remainingHeight > 0) {
        pdf.addPage();
      }
    }

    pdf.setPage(1);

    // Download PDF
    const filename = `cu_admit_card_${examRoll}_${unit}.pdf`
    pdf.save(filename)
  }

  /**
   * Cleanup temporary container
   */
  cleanup() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
  }
}

// Export singleton instance
export default new AdmitCardGenerator()
